import "./index.scss";
import React, { useState, useRef, useEffect } from "react";
import { FormControl } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { setWarnContent } from "@/store";
const HighlightColors = ["#7004DC"];

const DzReadHighlighter = ({ address, bookId, chapter, content }) => {
  const [highlights, setHighlights] = useState([]);
  const [selection, setSelection] = useState(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ x: 0, y: 0 });
  const [activeHighlight, setActiveHighlight] = useState(null);
  const [noteContent, setNoteContent] = useState("");
  const [showNote, setShowNote] = useState(false);
  const contentRef = useRef(null);
  const dispatch = useDispatch();

  // 从存储加载高亮数据
  useEffect(() => {
    const savedHighlights = localStorage.getItem(
      `book_highlights_${address}_${bookId}`
    );
    if (savedHighlights) {
      let chapterHighlights = [];
      try {
        chapterHighlights =
          JSON.parse(savedHighlights)[
            `book_${chapter.volume}_${chapter.chapter}`
          ]||[];
      } catch (error) {
        chapterHighlights = [];
      }
      setHighlights(chapterHighlights);
    }
  }, [content]);

  // 保存高亮数据
  useEffect(() => {
    let savedHighlights = {};
    try {
      savedHighlights = JSON.parse(
        localStorage.getItem(`book_highlights_${address}_${bookId}`) || "{}"
      );
    } catch (error) {}
    if (savedHighlights && chapter) {
      savedHighlights[`book_${chapter.volume}_${chapter.chapter}`] = highlights;
      localStorage.setItem(
        `book_highlights_${address}_${bookId}`,
        JSON.stringify(savedHighlights)
      );
    }
  }, [highlights]);
  // 处理文本选择
  const handleMouseUp = () => {
    const textSelection = window.getSelection();
    const selectedText = textSelection.toString().trim();
    if (selectedText.length === 0) {
      setSelection(null);
      if (noteContent) {
        saveNote();
      } else {
        cancelNote();
      }
      return;
    }

    if (activeHighlight) {
      return;
    }
    const range = textSelection.getRangeAt(0);
    const { startContainer, endContainer } = range;
    const rect = range.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();
    const isIncludesNote =
      startContainer.parentNode.dataset.highlightId ||
      endContainer.parentNode.dataset.highlightId;
    if (startContainer !== endContainer) {
      dispatch(setWarnContent("Note already exists!"));
      return;
    }
    if (isIncludesNote) {
      return;
    }
    setSelection({
      text: selectedText,
      range: {
        startOffset: range.startOffset,
        endOffset: range.endOffset,
        startContainerPath: getPathTo(range.startContainer),
        endContainerPath: getPathTo(range.endContainer),
      },
    });

    setToolbarPosition({
      x: rect.left - contentRect.left + rect.width / 2,
      y: rect.top - contentRect.top - 40,
    });
    setShowToolbar(true);
  };

  // 获取DOM节点路径
  const getPathTo = (node) => {
    const path = [];
    while (node && node !== contentRef.current) {
      path.unshift(Array.from(node.parentNode.childNodes).indexOf(node));
      node = node.parentNode;
    }
    return path;
  };

  // 根据路径恢复DOM节点
  const getNodeByPath = (path) => {
    let node = contentRef.current;
    for (const index of path) {
      if (node.childNodes[index]) {
        node = node.childNodes[index];
      } else {
        return null;
      }
    }
    return node;
  };

  // 添加高亮
  const addHighlight = async (isAi) => {
    if (!selection) return;
    const { range, text } = selection;
    const idx = range.startContainerPath[0];
    const startNode = getNodeByPath(range.startContainerPath);
    const endNode = getNodeByPath(range.endContainerPath);

    if (!startNode || !endNode) return;
    const note = isAi ? await Promise.resolve("ai mark") : "";
    const newHighlight = {
      id: Date.now(),
      text,
      color: HighlightColors[0],
      range,
      note,
      createdAt: new Date().toISOString(),
    };
    setNoteContent(note);
    const preList = highlights.slice(0, idx / 2);
    const nextList = highlights.slice(idx / 2).map((i) => {
      return {
        ...i,
        range: {
          ...i.range,
          endContainerPath: [i.range.endContainerPath[0] + 2],
          startContainerPath: [i.range.startContainerPath[0] + 2],
        },
      };
    });
    // 被插入的后一项起点终点减去插入步长
    if (nextList.length) {
      nextList[0].range.startOffset -= range.endOffset;
      nextList[0].range.endOffset -= range.endOffset;
    }
    setHighlights([...preList, newHighlight, ...nextList]);
    setActiveHighlight(newHighlight);
    setSelection(null);
    setShowToolbar(false);
    setShowNote(true);
    window.getSelection().removeAllRanges();
  };

  // 取消高亮
  const removeHighlight = (id) => {
    const removeTarget = highlights.find((i) => i.id === id);
    if (!removeTarget) return;

    // 获取要删除的高亮的容器路径
    const removeContainerPath = removeTarget.range.startContainerPath[0];
    const delStep = removeTarget.range.endOffset;
    let lastId = "";
    // 更新其他高亮的路径
    const updatedHighlights = highlights
      .map((item) => {
        if (item.id === id) {
          lastId = item.id;
          return item; // 将要删除的项保持不变
        }
        const curContainerPath = item.range.startContainerPath[0];
        // 若上一个高亮为被删除的项，且当前项高亮不在删除项高亮的前方，当前项累加上一项的步长
        if (id === lastId && curContainerPath > removeContainerPath) {
          lastId = item.id;
          return {
            ...item,
            range: {
              // ...item.range,
              startOffset: item.range.startOffset + delStep,
              endOffset: item.range.endOffset + delStep,
              startContainerPath: [
                curContainerPath - 2,
                ...item.range.startContainerPath.slice(1),
              ],
              endContainerPath: [
                curContainerPath - 2,
                ...item.range.endContainerPath.slice(1),
              ],
            },
          };
        }
        // 如果当前高亮在被删除高亮之后，且不是后一项，则调整高亮索引，每项-2
        if (id !== lastId && curContainerPath > removeContainerPath) {
          lastId = item.id;
          return {
            ...item,
            range: {
              ...item.range,
              startContainerPath: [
                curContainerPath - 2,
                ...item.range.startContainerPath.slice(1),
              ],
              endContainerPath: [
                curContainerPath - 2,
                ...item.range.endContainerPath.slice(1),
              ],
            },
          };
        }

        // 其余项保持
        lastId = item.id;
        return item;
      })
      .filter((h) => h.id !== id); // 最后过滤掉要删除的高亮

    setHighlights(updatedHighlights);

    setShowToolbar(false);
    setActiveHighlight(null);
    setShowNote(false);
    setNoteContent("");
  };
  // 保存笔记
  const saveNote = () => {
    if (!activeHighlight) return;

    setHighlights(
      highlights.map((h) =>
        h.id === activeHighlight.id ? { ...h, note: noteContent } : h
      )
    );
    setActiveHighlight(null);
    setNoteContent("");
    setShowNote(false);

    setShowToolbar(false);
    setNoteContent("");
  };
  const cancelNote = () => {
    if (activeHighlight && !activeHighlight.note) {
      removeHighlight(activeHighlight.id);
    } else {
      setShowNote(false);
      setNoteContent("");
      setShowToolbar(false);
      setActiveHighlight(null);
    }
  };
  // 渲染高亮效果
  useEffect(() => {
    if (!contentRef.current) return;

    // 清除旧的高亮
    const oldHighlights = contentRef.current.querySelectorAll(".highlight");
    oldHighlights.forEach((el) => {
      const parent = el.parentNode;
      parent.replaceChild(document.createTextNode(el.textContent), el);
      parent.normalize();
    });

    // 应用新的高亮
    highlights.forEach((highlight) => {
      const { range } = highlight;
      const startNode = getNodeByPath(range.startContainerPath);
      const endNode = getNodeByPath(range.endContainerPath);
      if (startNode === null || endNode === null) return;

      const newRange = document.createRange();
      newRange.setStart(startNode, range.startOffset);
      newRange.setEnd(endNode, range.endOffset);

      const span = document.createElement("span");
      span.className = "highlight";
      span.dataset.highlightId = highlight.id;
      span.style.backgroundColor = highlight.color;
      span.style.cursor = "pointer";
      span.style.fontStyle = "italic";
      try {
        newRange.surroundContents(span);
      } catch (e) {
        console.error("Failed to highlight:", e);
      }
    });

    // 添加高亮点击事件
    const highlightElements = contentRef.current.querySelectorAll(".highlight");
    highlightElements.forEach((el) => {
      el.onclick = (e) => {
        e.stopPropagation();
        const highlightId = parseInt(el.dataset.highlightId);
        const highlight = highlights.find((h) => h.id === highlightId);
        if (highlight) {
          setActiveHighlight(highlight);
          setNoteContent(highlight.note);

          const rect = el.getBoundingClientRect();
          const contentRect = contentRef.current.getBoundingClientRect();
          setToolbarPosition({
            x: rect.left - contentRect.left + rect.width / 2,
            y: rect.top - contentRect.top - 40,
          });
          setShowToolbar(true);
          setShowNote(true);
        }
      };
    });
  }, [highlights]);
  const aiBookMark = () => {
    dispatch(setWarnContent('Coming soon...'))
    // addHighlight(true);
  };
  const noteRef = useRef(null);
  useEffect(() => {
    if (showNote) {
      noteRef.current.focus();
      const length = noteRef.current.value.length;
      noteRef.current.setSelectionRange(length, length);
    }
  }, [showNote]);

  useEffect(() => {
    if (noteContent) {
      noteRef.current.style.height = noteRef.current.scrollHeight + "px";
    }
  }, [noteContent]);
  return (
    <div className="dz-read-container">
      <div
        ref={contentRef}
        className="content"
        onMouseUp={handleMouseUp}
        dangerouslySetInnerHTML={{ __html: content }}
      />

      {/* 工具栏 */}
      {showToolbar && (
        <div
          className="toolbar"
          style={{
            position: "absolute",
            left: toolbarPosition.x,
            top: toolbarPosition.y,
            transform: "translate(-50%,-65%)",
          }}
        >
          {activeHighlight?.note ? (
            <div className="dz-tips-box flex ">
              <div
                onClick={() => removeHighlight(activeHighlight.id)}
                className="highlight-btn"
              >
                DEL Highlight
              </div>
              |
              <div onClick={aiBookMark} className="ai-btn">
                AI Bookmark
              </div>
            </div>
          ) : (
            <div className="dz-tips-box flex ">
              <div onClick={() => addHighlight()} className="highlight-btn">
                Add Highlight
              </div>
              |
              <div onClick={aiBookMark} className="ai-btn">
                AI Bookmark
              </div>
            </div>
          )}
        </div>
      )}

      {/* 书签 */}
      {showNote && (
        <div
          className="toolbar"
          style={{
            position: "fixed",
            left: "80%",
            top: "70%",
            transform: "translate(-50%,-65%)",
            zIndex:'999999'
          }}
        >
          <div className="note-editor">
            <FormControl
              className="note-textarea"
              as="textarea"
              ref={noteRef}
              value={noteContent}
              onChange={(e) => {
                setNoteContent(e.target.value);
              }}
              placeholder="Add note..."
            />
            {/* <button onClick={cancelNote}>cancel</button>
            <button onClick={saveNote}>save</button> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default DzReadHighlighter;
